import { useTranslation } from 'react-i18next';
import { Alert, Box, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import { CancelOutlined, CheckCircleOutline, WarningAmber } from '@mui/icons-material';

import type { PasswordValidationResponse } from '@/generated/model';

import './PasswordStrengthFeedback.animation.css';

export function PasswordStrengthFeedback({
  newPassword,
  validation,
  isPending,
  validationColor,
}: {
  newPassword: string;
  validation?: PasswordValidationResponse;
  isPending: boolean;
  validationColor: string;
}) {
  const { t } = useTranslation();
  const progressValue = Math.min(120, Math.max(0, validation?.strength ?? 0));
  const validationDetailsColor = isPending ? 'text.disabled' : validationColor;
  const suggestionsColor = isPending ? 'text.disabled' : 'text.primary';
  const progressColor = isPending && validation !== undefined ? 'action.disabled' : validationColor;

  return (
    <Stack spacing={1} sx={{ mt: 1, mb: 1 }}>
      <Stack spacing={0.5}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '999px',
            ...(isPending && {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(105deg, transparent 25%, rgb(255 255 255 / 45%) 50%, transparent 75%)',
                transform: 'translateX(-100%)',
                animation: 'password-strength-shimmer 1.2s ease-in-out infinite',
                pointerEvents: 'none',
              },
              '@media (prefers-reduced-motion: reduce)': {
                '&::after': { animation: 'none' },
              },
            }),
          }}
        >
          <LinearProgress
            variant="determinate"
            value={(progressValue / 120) * 100}
            aria-valuemin={0}
            aria-valuemax={120}
            aria-valuenow={progressValue}
            aria-busy={isPending}
            sx={{
              height: 8,
              borderRadius: '999px',
              '& .MuiLinearProgress-bar': {
                backgroundColor: progressColor,
                borderRadius: 'inherit',
                transition: 'transform 250ms ease, background-color 250ms ease',
              },
              backgroundColor: 'action.disabledBackground',
            }}
            data-qa-element-id="password-strength-progress"
          />
        </Box>
        {validation && newPassword && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {validation.accepted ? (
              <CheckCircleOutline sx={{ color: validationDetailsColor, fontSize: 16 }} />
            ) : (
              <CancelOutlined sx={{ color: validationDetailsColor, fontSize: 16 }} />
            )}
            <Typography
              variant="caption"
              sx={{ color: validationDetailsColor, transition: 'color 250ms ease' }}
              data-qa-element-id="password-strength-status"
            >
              {validation.statusText}
            </Typography>
          </Stack>
        )}
      </Stack>
      {validation && newPassword && (
        <>
          <Collapse in={Boolean(validation.warning)} timeout="auto">
            <Alert
              severity="warning"
              icon={<WarningAmber fontSize="inherit" />}
              data-qa-element-id="password-strength-warning"
              sx={
                isPending
                  ? {
                      overflow: 'hidden',
                      position: 'relative',
                      '& .MuiAlert-icon': { color: 'text.disabled' },
                      backgroundColor: 'action.disabledBackground',
                      color: 'text.disabled',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(105deg, transparent 25%, rgb(255 255 255 / 45%) 50%, transparent 75%)',
                        transform: 'translateX(-100%)',
                        animation: 'password-strength-shimmer 1.2s ease-in-out infinite',
                        pointerEvents: 'none',
                      },
                      '@media (prefers-reduced-motion: reduce)': {
                        '&::after': { animation: 'none' },
                      },
                    }
                  : undefined
              }
            >
              {validation.warning}
            </Alert>
          </Collapse>
          <Collapse in={validation.suggestions.length > 0} timeout="auto">
            <Box sx={{ color: suggestionsColor }}>
              <Typography variant="caption" component="p">
                {t('login.resetPassword.passwordSuggestions')}
              </Typography>
              <Box
                component="ul"
                sx={{
                  '& li::marker': { color: 'currentColor', content: 'inherit' },
                  listStylePosition: 'outside',
                  listStyleType: 'disc',
                  m: 0,
                  pl: 3,
                  typography: 'caption',
                }}
                data-qa-element-id="password-strength-suggestions"
              >
                {validation.suggestions.map((suggestion) => (
                  <li key={suggestion}>{suggestion}</li>
                ))}
              </Box>
            </Box>
          </Collapse>
        </>
      )}
    </Stack>
  );
}
